import React, { useEffect, useRef } from "react";

import { containerId, needsContainer } from "../../utils/adConfig";
import { isYandexRefreshable, renderYandex, useAdRefresh } from "../../utils/adRefresh";

const NO_AD_TIMEOUT = 3000;

/**
 * Один вызов `Ya.Context.AdvManager.render` для любого яндексового плейсмента.
 *
 * Заменяет четыре компонента, которые различались только тем, откуда брать
 * контейнер: YandexAd (собирал `yandex_rtb_<blockId>`), AdvManager (брал
 * `option.renderTo`), YandexFullScreen (брал `option.blockId`) и YandexFloorAd
 * (контейнера не имел, зато умел `delay`).
 */
export function YandexAdapter({ entry, onNoAd = null, style = null, refreshable = true }) {
    const blockId = entry ? entry.blockId : null;
    const delay = entry ? entry.delay : null;
    const ref = useRef(null);
    // Как у adfox: блок обновляется и при уходе из экрана, иначе рефреш ждал бы
    // полный VIEW_DURATION непрерывного показа и при скролле почти не случался.
    const round = useAdRefresh(ref, {
        enabled: refreshable && Boolean(blockId) && needsContainer(entry) && isYandexRefreshable(entry.type),
        refreshOnLeave: true,
    });

    useEffect(() => {
        if (!blockId || typeof window === "undefined" || !window.yaContextCb) {
            return undefined;
        }

        const options = { blockId };

        if (entry.type) {
            options.type = entry.type;
        }
        if (entry.platform) {
            options.platform = entry.platform;
        }
        if (needsContainer(entry)) {
            options.renderTo = containerId(entry);
        }
        // РСЯ бывает оставляет блок пустым, не позвав ни altCallback, ни onError:
        // без onRender за NO_AD_TIMEOUT место считается пустым.
        let rendered = false;
        let noAdTimer = null;

        if (onNoAd) {
            options.altCallback = onNoAd;
            options.onError = onNoAd;
            options.onRender = () => {
                rendered = true;
            };
        }

        const render = () => {
            renderYandex(options, ref.current, round);

            // Отсчёт — с момента, когда РСЯ разобрала вызов из очереди, а не пока грузится её скрипт.
            if (onNoAd) {
                window.yaContextCb.push(() => {
                    noAdTimer = setTimeout(() => {
                        if (!rendered) {
                            onNoAd();
                        }
                    }, NO_AD_TIMEOUT);
                });
            }
        };

        // Задержка — только у первого показа: рефреш и так ждёт, пока блок провисит в экране.
        if (!delay || round) {
            render();

            return () => clearTimeout(noAdTimer);
        }

        const timer = setTimeout(render, delay);

        return () => {
            clearTimeout(timer);
            clearTimeout(noAdTimer);
        };
    }, [blockId, delay, round]);

    if (!entry || !blockId || !needsContainer(entry)) {
        return null;
    }

    return <div ref={ref} id={containerId(entry)} style={style} />;
}

export default YandexAdapter;
